import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPosSizingSyncPreferences } from '../../../utils/ApiCall';

export default function usePosSizingSyncPreferences(userUid, onError) {
    const [preference, setPreference] = useState(null);
    const ownerRef = useRef(null);
    useEffect(() => {
        const owner = { userUid, active: true, version: 0 };
        ownerRef.current = owner;
        setPreference(null);
        if (!userUid) return undefined;
        let reading = false;
        const refresh = async () => {
            if (reading || !owner.active) return;
            reading = true;
            const version = owner.version;
            try {
                const result = await fetchPosSizingSyncPreferences();
                if (typeof result?.enabled !== 'boolean') throw new Error('Invalid sync preference.');
                if (owner.active && ownerRef.current === owner && owner.version === version) {
                    setPreference({ userUid, enabled: result.enabled });
                }
            } catch {
                if (owner.active && ownerRef.current === owner) {
                    onError?.('Could not load Position Sizing auto-sync. Automatic updates are paused.');
                }
            } finally {
                reading = false;
            }
        };
        void refresh();
        const visible = () => { if (document.visibilityState === 'visible') void refresh(); };
        document.addEventListener('visibilitychange', visible);
        return () => {
            owner.active = false;
            document.removeEventListener('visibilitychange', visible);
        };
    }, [onError, userUid]);
    // Settings dispatches only after the server has saved the preference. A
    // delayed initial read must not overwrite that confirmed choice.
    const setEnabled = useCallback((enabled) => {
        if (ownerRef.current?.userUid === userUid) ownerRef.current.version += 1;
        setPreference({ userUid, enabled: Boolean(enabled) });
    }, [userUid]);
    return {
        enabled: Boolean(userUid && preference?.userUid === userUid && preference.enabled),
        ready: Boolean(userUid && preference?.userUid === userUid),
        setEnabled,
    };
}
